// ERRTRACK1 (docs/build-briefs/ERRTRACK1-error-tracking.md; owner ruling DEC-514 item 3). Browser side of error
// tracking, shared by the location and floor page, the franchisor console and the Snip & Sip Admin console.
//
//   - Catches window errors and unhandled promise rejections, and watches fetch for a server 5xx that carries a
//     reference code (the server has already logged those; they are only shown here).
//   - Shows one failure notice: a plain sentence and the short code. Consoles also get Copy code. The floor shows the
//     code in large type with Try again and nothing to copy, no report form and no link (floor staff tell a manager).
//   - Posts a compact report to POST /client-errors: ONLY route, build revision, tenant id, location id, error name,
//     message, stack and the code. No page content, no form values, no URL query, no storage, no cookies (the request
//     is sent without credentials). The server keeps the same allow-list and scrubs again.
//   - At most 5 reports a minute from one page, and the same error is posted once a minute, so a loop cannot flood.

import { createFailureState } from "./failureState.ts";
import { bundledRevision } from "./newVersionCheck.ts";

export type ErrorTrackingConsole = "location" | "franchisor" | "admin";

export interface ErrorTrackingOptions {
  readonly console: ErrorTrackingConsole;
  /** Which kind of screen shows right now (the location page holds both). */
  readonly surface: () => "console" | "floor";
  /** Opaque ids for the report, when the page has them. */
  readonly ids?: () => { tenantId?: string | undefined; locationId?: string | undefined };
  /** The screen the person is on, as a path such as "/ring". Defaults to the page path plus the in-page route. */
  readonly screen?: () => string | undefined;
}

/** SUPPORT1: the event Report this raises; ui/src/supportHelp.ts opens the Report a problem form on it. */
export const SUPPORT_REPORT_EVENT = "support:report-problem";
const ALPHABET = "0123456789ABCDEFGHJKMNPQRSTVWXYZ";
const MAX_REPORTS_PER_MINUTE = 5;
const NOTICE_ID = "error-notice";
const NOTICE_SHOW_MS = 30_000;
const FLOOR_HINT = "Tell your manager if it keeps happening.";

/** A new code such as K7M2-94QX (the device setup code alphabet: no I, L, O or U). */
export function newReferenceCode(random: (n: number) => Uint8Array = defaultRandom): string {
  const bytes = random(8);
  let out = "";
  for (let i = 0; i < 8; i += 1) {
    out += ALPHABET[(bytes[i] ?? 0) % ALPHABET.length];
    if (i === 3) out += "-";
  }
  return out;
}

function defaultRandom(n: number): Uint8Array {
  const bytes = new Uint8Array(n);
  crypto.getRandomValues(bytes);
  return bytes;
}

/** True for noise that is not a defect in our page. */
function isIgnorable(message: string): boolean {
  return /ResizeObserver loop/i.test(message);
}

function describe(value: unknown): { name: string; message: string; stack: string } {
  if (value instanceof Error) {
    return { name: value.name, message: value.message, stack: value.stack ?? "" };
  }
  if (typeof value === "string") return { name: "Error", message: value, stack: "" };
  return { name: "Error", message: "Unhandled rejection with a non-error value", stack: "" };
}

let started = false;

export function startErrorTracking(options: ErrorTrackingOptions): void {
  if (started) return;
  if (window.location.protocol === "file:" || window.location.origin === "null") return;
  started = true;

  const sentAt: number[] = [];
  const lastByKey = new Map<string, number>();
  let hideTimer: number | undefined;

  const route = (): string => {
    const custom = options.screen?.();
    if (custom !== undefined && custom !== "") return custom;
    const hash = window.location.hash.startsWith("#/") ? window.location.hash.slice(1) : "";
    return `${window.location.pathname}${hash}`;
  };

  const showNotice = (reference: string): void => {
    const floor = options.surface() === "floor";
    document.getElementById(NOTICE_ID)?.remove();
    if (hideTimer !== undefined) window.clearTimeout(hideTimer);
    const card = createFailureState({
      sentence: "Something went wrong.",
      referenceCode: reference,
      allowCopy: !floor,
      // SUPPORT1: consoles only (the floor has no report form); supportHelp.ts listens for this and opens the form.
      onReport: floor ? undefined : () => window.dispatchEvent(new CustomEvent(SUPPORT_REPORT_EVENT)),
      codeHint: floor ? FLOOR_HINT : undefined,
      onRetry: () => window.location.reload(),
      testid: "error-notice-card",
    });
    const wrap = document.createElement("div");
    wrap.id = NOTICE_ID;
    wrap.className = floor ? "error-notice error-notice-floor" : "error-notice";
    wrap.dataset["testid"] = NOTICE_ID;
    wrap.append(card);
    document.body.append(wrap);
    hideTimer = window.setTimeout(() => wrap.remove(), NOTICE_SHOW_MS);
  };

  const post = (reference: string, name: string, message: string, stack: string, status?: number): void => {
    const now = Date.now();
    while (sentAt.length > 0 && now - (sentAt[0] ?? 0) > 60_000) sentAt.shift();
    if (sentAt.length >= MAX_REPORTS_PER_MINUTE) return;
    const key = `${name}|${message}|${stack.split("\n")[1] ?? ""}`;
    const last = lastByKey.get(key);
    if (last !== undefined && now - last < 60_000) return;
    lastByKey.set(key, now);
    if (lastByKey.size > 50) lastByKey.delete(lastByKey.keys().next().value as string);
    sentAt.push(now);
    const ids = options.ids?.() ?? {};
    const body = {
      reference,
      console: options.console,
      route: route(),
      buildRevision: bundledRevision(),
      ...(ids.tenantId === undefined ? {} : { tenantId: ids.tenantId }),
      ...(ids.locationId === undefined ? {} : { locationId: ids.locationId }),
      ...(status === undefined ? {} : { status }),
      errorName: name,
      message,
      stack,
    };
    // Fire and forget: reporting must never raise an error of its own.
    void fetch("/client-errors", {
      method: "POST",
      headers: { "content-type": "application/json" },
      body: JSON.stringify(body),
      credentials: "omit",
      keepalive: true,
    }).catch(() => undefined);
  };

  const handle = (value: unknown, fallbackMessage: string): void => {
    const info = describe(value);
    const message = info.message === "" ? fallbackMessage : info.message;
    if (isIgnorable(message)) return;
    const reference = newReferenceCode();
    showNotice(reference);
    post(reference, info.name, message, info.stack);
  };

  window.addEventListener("error", (event) => {
    if (!(event instanceof ErrorEvent)) return;
    handle(event.error ?? event.message, event.message);
  });
  window.addEventListener("unhandledrejection", (event) => {
    handle(event.reason, "Unhandled promise rejection");
  });

  // A server 5xx the server already logged: show its code. Nothing is posted (the server has the report).
  const original = window.fetch.bind(window);
  window.fetch = async (...args: Parameters<typeof fetch>): Promise<Response> => {
    const response = await original(...args);
    if (response.status >= 500) {
      try {
        const body: unknown = await response.clone().json();
        const record = typeof body === "object" && body !== null ? (body as Record<string, unknown>) : {};
        if (record["error"] === "internal_error" && typeof record["reference"] === "string") {
          showNotice(record["reference"]);
        }
      } catch {
        // A body that is not JSON carries no code; stay quiet.
      }
    }
    return response;
  };
}
